import api from "./api";
import store from "@/store";
import { USER_ROLES } from "@/services/constants";

const userService = {};
const endpoint = () => `/users`;

userService.create = function (user) {
  return api.post(endpoint(), user);
};

/**
 * @description Alta pública de un socio contra el motor externo de
 *   elegibilidad del tenant (`POST /users/self-signup`, sin auth). Crea
 *   Member+Membership+User del lado del backend — NO devuelve token
 *   (la invitación para definir contraseña llega por email/WhatsApp, no
 *   autologuea). Solo disponible para tenants con `eligibility.engine`
 *   configurado (ej. Corporación Médica / Dyntech).
 * @param {Object} body - { dni, bornAt, email, firstName, lastName }
 * @param {Object} [options] - opciones de la request (ej. `getErrorMessage`
 *   para que el form maneje ciertos códigos sin el toast genérico, UC-REG-291)
 * @returns {Promise<{success: boolean}>}
 */
userService.selfSignup = function (body, options) {
  return api
    .post(`${endpoint()}/self-signup`, body, options)
    .then((res) => res.data);
};

userService.get = function (query) {
  return api
    .get(endpoint(), {
      params: { ...query, sort: { lastName: 1, firstName: 1 } },
    })
    .then((res) => res.data);
};

/**
 * @description UC-REG-219 — Lista de prestadores ordenada por número de
 *   prestador (doctor.providerCode = PR-NNNNNN, zero-padded → orden string
 *   == orden numérico). lastName queda como desempate para prestadores sin
 *   código todavía (pre-backfill).
 */
userService.getDoctors = function (query = {}) {
  return userService.get({
    ...query,
    roles: [USER_ROLES.DOCTOR.id],
    sort: { "doctor.providerCode": 1, lastName: 1 },
  });
};

userService.getDoctorsByInstitution = function (institutionId) {
  return api
    .get(`doctors/institution/${institutionId}`, {
      params: { sort: { lastName: 1, firstName: 1 } },
    })
    .then((res) => res.data);
};

userService.getManagers = function (query = {}) {
  return userService.get({
    ...query,
    roles: [USER_ROLES.MANAGER.id],
    sort: { lastName: 1, firstName: 1 },
  });
};

userService.getById = function (id) {
  return api.get(`${endpoint()}/${id}`).then((res) => res.data);
};

userService.update = function (userFormData) {
  const userId = userFormData.get("_id");
  return api.put(`${endpoint()}/${userId}`, userFormData);
};

/**
 * @description Actualiza campos del usuario autenticado via PUT /users/me.
 * Solo acepta campos seguros (email, phone, governmentId, doctor.description).
 * Mergea la respuesta con el usuario existente en el store para no perder
 * campos que no devuelve el backend (role display, permissions, etc).
 * @param {Object} payload - Campos a actualizar
 * @returns {Promise<void>}
 */
userService.updateMe = function (payload) {
  return api.put(`${endpoint()}/me`, payload).then(() => {
    const existing = store.state.user;
    store.commit("setUser", { ...existing, ...payload });
  });
};

userService.updatePassword = function (user) {
  return api.put(`/users/${user._id}/password-reset`, user);
};

/**
 * @description UC-REG-097 — Envía email de "blanquear clave" al usuario.
 *              Usa el endpoint admin-tool `PUT /users/:id/admin-password-reset`
 *              (no el público `/:username/password-reset` que es silencioso).
 *              Si el user no existe o no es de la org del admin, devuelve
 *              404 → el admin ve un error real en vez de creer que envió.
 * @param {string} userId - User._id (no username)
 * @returns {Promise<void>}
 */
userService.sendPasswordReset = function (userId) {
  return api.put(`${endpoint()}/${userId}/admin-password-reset`);
};

userService.lock = function (id) {
  return api.put(`${endpoint()}/${id}/lock`);
};

userService.unlock = function (id) {
  return api.put(`${endpoint()}/${id}/unlock`);
};

userService.delete = function (id) {
  return api.delete(`${endpoint()}/${id}`);
};

userService.updateSelectedBox = function (userId, boxId) {
  return api
    .put(`${endpoint()}/${userId}/selected-box`, { boxId })
    .then((res) => res.data);
};

export default userService;
